import { useState, useEffect, useCallback } from "react";
import api from "@/utils/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import UpgradeModal from "@/components/UpgradeModal";
import UpgradeRequiredView from "@/components/UpgradeRequiredView";
import UpgradePrompt from "@/components/UpgradePrompt";
import { Calculator as CalculatorIcon } from 'lucide-react';
import { useIsPro } from '@/hooks/useIsPro';
import YearSelector from "@/components/YearSelector";
import AuditBreakdownPanel from "@/components/AuditBreakdownPanel";
import CollapsibleHint from "@/components/CollapsibleHint";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Calculator, Info, TrendingUp, TrendingDown, DollarSign, HelpCircle } from "lucide-react";

// Tooltip component for calculation steps
const TaxTooltip = ({ step, t }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  if (!step) return null;
  
  return (
    <div className="relative inline-block">
      <button
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        onClick={() => setIsOpen(!isOpen)}
        className="ml-2 text-gray-400 hover:text-emerald-600 transition-colors"
        type="button"
      >
        <HelpCircle className="h-4 w-4" />
      </button>
      
      {isOpen && (
        <div className="absolute z-50 left-0 top-6 w-96 p-4 bg-white border border-gray-200 rounded-xl shadow-xl">
          <div className="space-y-3">
            <div>
              <p className="font-semibold text-sm text-gray-900 mb-1">{t('taxes.calculation.formula')}</p>
              <p className="text-xs text-gray-600 font-mono bg-gray-50 p-2 rounded">{step.formula}</p>
            </div>
            
            <div>
              <p className="font-semibold text-sm text-gray-900 mb-1">{t('taxes.calculation.explanation')}</p>
              <p className="text-xs text-gray-700">{step.explanation}</p>
            </div>
            
            {step.limitations && (
              <div className="border-t pt-2">
                <p className="font-semibold text-sm text-gray-700 mb-1">
                  {t('taxes.calculation.limitations')}
                </p>
                <p className="text-xs text-gray-600">{step.limitations}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function Taxes() {
  const { user, logGuestEvent } = useAuth();
  const { t } = useTranslation();

  // Gast-Trichter: Steuern-Ansicht geöffnet (starkes USP-Intent-Signal)
  useEffect(() => {
    if (user?.is_guest && logGuestEvent) logGuestEvent("taxes_viewed");
  }, [user?.is_guest, logGuestEvent]);
  
  const [taxData, setTaxData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [country, setCountry] = useState("DE");
  
  // AT Investment Allowance Slider state
  const [investmentAllowance, setInvestmentAllowance] = useState(null);
  const [investmentAllowanceInput, setInvestmentAllowanceInput] = useState('');
  const [isUpdatingSettings, setIsUpdatingSettings] = useState(false);
  const [hintsCollapsed, setHintsCollapsed] = useState({});
  const toggleHint = async (id) => {
    const next = { ...hintsCollapsed, [id]: !hintsCollapsed[id] };
    setHintsCollapsed(next);
    try { await api.put('/settings', { ui_hints_collapsed: next }); } catch (e) { console.error('Hint-Status speichern fehlgeschlagen', e); }
  };

  // Check if user has access to tax overview (Iter 73j: useIsPro berücksichtigt has_lifetime_access)
  const hasTaxAccess = useIsPro();

  // Settings update function with immediate recalculation
  const updateSettings = async (amount) => {
    setIsUpdatingSettings(true);
    try {
      await api.put('/settings', {
        at_investment_allowance_amount: amount
      });
      // Recalculate taxes after saving
      await calculateTaxes();
    } catch (error) {
      console.error('Error saving investment allowance setting:', error);
      toast.error('Fehler beim Speichern der Einstellung');
    } finally {
      setIsUpdatingSettings(false);
    }
  };

  // Handle slider change with immediate local update, commit on mouse up
  // Live-Ersparnis: lineare Interpolation auf der Backend-Kurve (ESt stückweise linear)
  const ifbSavingsFor = (val) => {
    const curve = taxData?.at_ifb_savings_curve;
    if (!curve?.length) return null;
    if (val <= curve[0][0]) return curve[0][1];
    for (let i = 1; i < curve.length; i++) {
      const [a1, s1] = curve[i - 1];
      const [a2, s2] = curve[i];
      if (val <= a2) return a2 === a1 ? s2 : s1 + ((val - a1) / (a2 - a1)) * (s2 - s1);
    }
    return curve[curve.length - 1][1];
  };

  const handleSliderChange = (newAmount) => {
    setInvestmentAllowance(newAmount);
    setInvestmentAllowanceInput(newAmount.toString());
  };

  // Handle slider commit (mouse up / touch end)
  const handleSliderCommit = async (newAmount) => {
    await updateSettings(newAmount);
  };

  // Handle input field change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInvestmentAllowanceInput(value);
    
    // Update slider position if valid number
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      setInvestmentAllowance(numValue);
    }
  };

  // Handle input commit (blur or enter)
  const handleInputCommit = async () => {
    const numValue = parseFloat(investmentAllowanceInput);
    const maxAmount = taxData?.at_max_investment_allowance || 0;
    
    if (isNaN(numValue) || numValue < 0) {
      // Reset to current value if invalid
      setInvestmentAllowanceInput(investmentAllowance?.toString() || '0');
      return;
    }
    
    // Clamp to valid range
    const clampedValue = Math.min(Math.max(numValue, 0), maxAmount);
    setInvestmentAllowance(clampedValue);
    setInvestmentAllowanceInput(clampedValue.toString());
    
    if (clampedValue !== numValue) {
      toast.info(`Wert auf Maximum ${formatCurrency(maxAmount)} begrenzt`);
    }
    
    await updateSettings(clampedValue);
  };

  // Handle input key press (Enter to commit)
  const handleInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleInputCommit();
    }
  };

  const calculateTaxes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(
        `/tax/calculate?year=${selectedYear}`
      );
      setTaxData(response.data);
      if (response.data.country) {
        setCountry(response.data.country);
      }
      
      // Initialize investment allowance slider for AT users
      if (response.data.country === "AT" && response.data.at_current_investment_allowance !== undefined) {
        setInvestmentAllowance(response.data.at_current_investment_allowance);
        setInvestmentAllowanceInput(response.data.at_current_investment_allowance.toString());
      }
    } catch (error) {
      console.error("Error calculating taxes:", error);
      toast.error(t('taxes.calculationError'));
    } finally {
      setLoading(false);
    }
  }, [selectedYear, t]);

  // Load user's saved investment allowance setting on component mount
  useEffect(() => {
    const loadUserSettings = async () => {
      try {
        const prefs = await api.get('/settings');
        if (prefs.data?.ui_hints_collapsed) setHintsCollapsed(prefs.data.ui_hints_collapsed);
      } catch (e) { /* Hinweis-Status optional */ }
      if (user && user.country === "AT") {
        try {
          const response = await api.get('/settings');
          if (response.data.at_investment_allowance_amount !== undefined) {
            setInvestmentAllowance(response.data.at_investment_allowance_amount);
            setInvestmentAllowanceInput(response.data.at_investment_allowance_amount.toString());
          }
        } catch (error) {
          console.error('Error loading user settings:', error);
        }
      }
    };
    
    loadUserSettings();
  }, [user]);

  useEffect(() => {
    // Get user's country
    if (user && user.country) {
      setCountry(user.country);
    }
    if (hasTaxAccess) {
      calculateTaxes();
    }
  }, [user, selectedYear, hasTaxAccess, calculateTaxes]); // Add calculateTaxes as dependency
  
  // Show upgrade prompt if no access
  if (!hasTaxAccess) {
    return (
      <UpgradeRequiredView
        source="taxes"
        icon={CalculatorIcon}
        title="Steuer-Berechnung"
        subtitle="Automatische Berechnung deiner Steuerlast für DE, AT und CH — ab Pro verfügbar."
        features={[
          'Live-Berechnung Einkommensteuer, USt., GewSt.',
          'Vorauszahlungen und Quartalsplanung',
          'Belege automatisch zugeordnet',
          'Direkt-Export für ELSTER/DATEV',
          'Steuerberater-konforme Berichte',
        ]}
      />
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  // Gast-Demo: Steuern ist read-only. Jeder Klick auf ein Bedien-Element wird
  // zentral abgefangen → kein Drawer/Dialog öffnet, keine Schreib-Aktion läuft;
  // stattdessen der "nur im Live-Modus"-Hinweis + "Konto sichern"-Moment.
  const _guestActionSel = 'button, a, [role="button"], [role="switch"], [role="checkbox"], [role="tab"], input, select, textarea';
  const guestFireReadonly = () => {
    try {
      window.dispatchEvent(new CustomEvent('ivoliq:guest-readonly', {
        detail: {
          code: 'guest_readonly',
          message: 'Nur im Live-Modus verfügbar. Sichere dir dein kostenloses Konto, um deine Steuern selbst zu berechnen.',
        },
      }));
    } catch { /* ignore */ }
  };
  const guestBlockPointer = (e) => {
    if (!user?.is_guest) return;
    if (!e.target?.closest?.(_guestActionSel)) return;
    e.preventDefault();
    e.stopPropagation();
  };
  const guestBlockClick = (e) => {
    if (!user?.is_guest) return;
    if (!e.target?.closest?.(_guestActionSel)) return;
    e.preventDefault();
    e.stopPropagation();
    guestFireReadonly();
  };

  if (loading && !taxData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="taxes-page" onPointerDownCapture={guestBlockPointer} onClickCapture={guestBlockClick}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-1" style={{ fontFamily: "Manrope, sans-serif" }}>
            {t('taxes.taxCalculation')} {selectedYear}
          </h1>
          <p className="text-sm text-slate-500">
            {country === "AT" 
              ? t('taxes.provisionalAustrianTax') 
              : country === "DE"
              ? t('taxes.provisionalGermanTax')
              : t('taxes.taxCalculation')} für das Jahr {selectedYear}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <YearSelector 
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
            minYear={2020}
            maxYear={2030}
          />
          <a 
            href="/tax-documentation" 
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-xl transition-colors"
          >
            <Info className="h-5 w-5" />
            <span className="font-medium">{t('taxes.howItWorks')}</span>
          </a>
        </div>
      </div>

      {/* Warning for unsupported countries (only OTHER, not CH) */}
      {country === "OTHER" && (
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-3">
              <Info className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-orange-900">
                <p className="font-semibold mb-2">
                  {t("taxes.noCalculation", "Automatic tax calculation not available")}
                </p>
                <p className="mb-2">
                  {t("taxes.noCalculationText", "No specific tax calculations available for your country.")}
                </p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>{t("taxes.manualEntry", "Bitte tragen Sie Ihre Steuern manuell in Ihre Buchhaltung ein")}</li>
                  <li>{t("taxes.consultTaxAdvisor", "Consult a tax advisor for accurate calculations")}</li>
                  <li>{t("taxes.useGenericVAT", "You can use generic VAT rates in invoices")}</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Info for Switzerland - Canton required */}
      {country === "CH" && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-3">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-semibold mb-2">
                  🇨🇭 {t("taxes.swissTaxes", "Schweizer Steuerberechnung")}
                </p>
                <p className="mb-2">
                  {t("taxes.swissInfo", "The tax calculation includes federal tax, cantonal tax, and AHV/IV/EO contributions.")}
                </p>
                <p className="text-xs text-blue-700">
                  {t("taxes.swissNote", "Note: Please ensure your canton is correctly selected in settings.")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Banner - General note for all supported countries */}
      {country && ["DE", "AT", "CH"].includes(country) && (
        <CollapsibleHint id="disclaimer" title={t('taxes.disclaimer')} collapsed={!!hintsCollapsed.disclaimer} onToggle={() => toggleHint('disclaimer')} tone="blue">
          <p>{t('taxes.disclaimerText')}</p>
        </CollapsibleHint>
      )}

      {/* Feb 2026 — UStVA-Konsistenz-Hinweis + Audit-Breakdown auf EINER Card,
          Hinweis einklappbar (Header klappt Detail auf/zu). */}
      {taxData?.audit_breakdown && (
        <AuditBreakdownPanel
          breakdown={taxData.audit_breakdown}
          currencyFormatter={formatCurrency}
          headerTitle="USt-Werte = UStVA-konform · Gewinn = EStG-konform"
          headerNote={taxData?.ustva_consistency_note || null}
          noteCollapsed={!!hintsCollapsed.ustva}
          onToggleNote={() => toggleHint('ustva')}
        />
      )}

      {/* AT Investment Allowance: immer für AT einblenden; Slider nur bei Gewinn > 33.000 €, sonst Hinweis */}
      {country === "AT" && taxData && (
        <Card>
          <CardHeader>
            <CardTitle>{t('taxes.parameters.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            {taxData.at_max_investment_allowance > 0 ? (
            <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-xl">
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-semibold text-gray-900">
                    Investitionsfreibetrag (§10 EStG)
                  </Label>
                  <p className="text-xs text-gray-600 mt-1">
                    Wählen Sie den Betrag, den Sie tatsächlich investieren möchten
                  </p>
                </div>
                
                <div className="space-y-4">
                  {/* Combined Slider and Input */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                    {/* Slider Section */}
                    <div className="md:col-span-2 space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">0€</span>
                        <span className="text-gray-500">{formatCurrency(taxData.at_max_investment_allowance)}</span>
                      </div>
                      
                      <Slider
                        value={[investmentAllowance || 0]}
                        onValueChange={(value) => handleSliderChange(value[0])}
                        onValueCommit={(value) => handleSliderCommit(value[0])}
                        max={taxData.at_max_investment_allowance}
                        min={0}
                        step={100}
                        className="w-full"
                        disabled={loading || isUpdatingSettings}
                      />
                    </div>
                    
                    {/* Input Section */}
                    <div className="space-y-2">
                      <Label className="text-xs text-gray-600">Exakter Betrag</Label>
                      <div className="relative">
                        <Input
                          type="number"
                          value={investmentAllowanceInput}
                          onChange={handleInputChange}
                          onBlur={handleInputCommit}
                          onKeyPress={handleInputKeyPress}
                          min={0}
                          max={taxData.at_max_investment_allowance}
                          step={100}
                          className="pr-8"
                          disabled={loading || isUpdatingSettings}
                          placeholder="0"
                        />
                        <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-gray-500">€</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-sm text-gray-700 bg-blue-50 border border-blue-200 p-3 rounded">
                    <p>
                      <strong>Investition erforderlich:</strong> Für den investitionsbedingten Freibetrag müssen Sie mindestens {formatCurrency(investmentAllowance || 0)} in begünstigtes Anlagevermögen investieren (Behaltefrist: 4 Jahre).
                    </p>
                    {ifbSavingsFor(investmentAllowance || 0) != null && (
                      <p className="mt-1.5 font-medium text-emerald-700" data-testid="taxes-ifb-savings">
                        Spart {formatCurrency(ifbSavingsFor(investmentAllowance || 0))} Einkommensteuer.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            ) : (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="ifb-threshold-hint">
                <p className="text-sm font-semibold text-amber-900 mb-1">
                  Investitionsfreibetrag (§10 EStG) derzeit nicht verfügbar
                </p>
                <p className="text-sm text-amber-800 mb-3">
                  Der investitionsbedingte Gewinnfreibetrag greift erst ab einem <strong>Gewinn über 33.000 €</strong> –
                  maßgeblich ist der Gewinn <strong>nach Abzug der SVS-Beiträge</strong> (vor Einkommensteuer).
                  Bis dahin wird automatisch der <strong>Grundfreibetrag (15 %)</strong> berücksichtigt – dafür ist keine Investition nötig.
                </p>
                <div className="text-sm bg-white/70 border border-amber-200 rounded-lg p-3 space-y-1" data-testid="ifb-threshold-breakdown">
                  <div className="flex justify-between">
                    <span className="text-amber-900">Gewinn vor SVS</span>
                    <span className="font-medium text-amber-900">{formatCurrency(taxData.taxable_income || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-amber-900">− SVS-Beiträge</span>
                    <span className="font-medium text-amber-900">−{formatCurrency(taxData.social_security || 0)}</span>
                  </div>
                  <div className="flex justify-between border-t border-amber-200 pt-1">
                    <span className="font-semibold text-amber-900">= Gewinn nach SVS (maßgeblich)</span>
                    <span className="font-semibold text-amber-900">{formatCurrency(Math.max(0, (taxData.taxable_income || 0) - (taxData.social_security || 0)))}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-amber-900">Schwelle für den IFB</span>
                    <span className="text-amber-900">{formatCurrency(33000)}</span>
                  </div>
                  <div className="flex justify-between border-t border-amber-200 pt-1">
                    <span className="font-semibold text-amber-900">Noch erforderlich (Gewinn nach SVS)</span>
                    <span className="font-semibold text-amber-900">+{formatCurrency(Math.max(0, 33000 - Math.max(0, (taxData.taxable_income || 0) - (taxData.social_security || 0))))}</span>
                  </div>
                </div>
                <p className="text-xs text-amber-700 mt-2">
                  Sobald dein Gewinn nach SVS über 33.000 € liegt, kannst du hier den investitionsbedingten Freibetrag einstellen (Investition in begünstigtes Anlagevermögen, 4 Jahre Behaltefrist).
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Info Cards for DE and CH */}
      {country === "DE" && (
        <CollapsibleHint id="params" title="Berechnungsparameter" collapsed={!!hintsCollapsed.params} onToggle={() => toggleHint('params')} tone="slate">
          <p>Gewerbesteuer- und Kirchensteuer-Einstellungen können in den <a href="/settings" className="text-blue-600 underline hover:text-blue-700">Einstellungen</a> konfiguriert werden. Die Berechnung erfolgt automatisch.</p>
        </CollapsibleHint>
      )}

      {country === "CH" && (
        <CollapsibleHint id="params" title="Berechnungsparameter" collapsed={!!hintsCollapsed.params} onToggle={() => toggleHint('params')} tone="slate">
          <p>Die Steuerberechnung erfolgt automatisch basierend auf Ihren Einnahmen und Ausgaben.</p>
        </CollapsibleHint>
      )}

      {taxData && (
        <>
          {/* Overview Cards - Swiss & High-Contrast Design */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <Card data-testid="income-card">
              <CardContent className="p-5 sm:p-6">
                <p className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500 mb-1">
                  {t('taxes.overview.income')}
                </p>
                <p className="text-xs text-slate-400 mb-1">{t('taxes.overview.gross')}</p>
                <p className="text-2xl sm:text-3xl font-semibold font-mono tracking-tight text-slate-900" data-testid="total-income-display">
                  {formatCurrency(taxData.total_income)}
                </p>
                <div className="pt-3 mt-3 border-t border-slate-100 flex justify-between items-baseline">
                  <span className="text-xs text-slate-500">{t('taxes.overview.net')}</span>
                  <span className="text-base font-semibold text-slate-700">{formatCurrency(taxData.total_income_netto || 0)}</span>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="expenses-card">
              <CardContent className="p-5 sm:p-6">
                <p className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500 mb-1">
                  {t('taxes.overview.expenses')}
                </p>
                <p className="text-xs text-slate-400 mb-1">{t('taxes.overview.gross')}</p>
                <p className="text-2xl sm:text-3xl font-semibold font-mono tracking-tight text-slate-900" data-testid="total-expenses-display">
                  {formatCurrency(taxData.total_expenses)}
                </p>
                <div className="pt-3 mt-3 border-t border-slate-100 flex justify-between items-baseline">
                  <span className="text-xs text-slate-500">{t('taxes.overview.net')}</span>
                  <span className="text-base font-semibold text-slate-700">{formatCurrency(taxData.total_expenses_netto || 0)}</span>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="profit-card">
              <CardContent className="p-5 sm:p-6">
                <p className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500 mb-1">
                  {t('taxes.overview.profit')}
                </p>
                <p className="text-xs text-slate-400 mb-1">{t('taxes.overview.beforeTaxes')}</p>
                <p className="text-2xl sm:text-3xl font-semibold font-mono tracking-tight text-slate-900" data-testid="taxable-income-display">
                  {formatCurrency(taxData.taxable_income)}
                </p>
              </CardContent>
            </Card>

            <Card data-testid="net-profit-card">
              <CardContent className="p-5 sm:p-6">
                <p className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500 mb-1">
                  {t('taxes.overview.netAvailable')}
                </p>
                <p className="text-xs text-slate-400 mb-1">{t('taxes.overview.afterTaxes')}</p>
                <p className="text-2xl sm:text-3xl font-semibold font-mono tracking-tight text-slate-900" data-testid="net-profit-display">
                  {formatCurrency(taxData.net_profit)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* VAT Breakdown */}
          <Card className="border border-slate-200 bg-white shadow-sm rounded-xl">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="text-base font-bold text-slate-950 tracking-tight">
                {t('taxes.vat.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                <div className="p-5 border border-slate-200 rounded-xl bg-slate-50 flex flex-col justify-between">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.05em] text-slate-500 mb-2">{t('taxes.vat.collected')}</div>
                    <div className="text-2xl sm:text-3xl font-black tracking-tight text-slate-950">
                      {formatCurrency(taxData.vat_collected)}
                    </div>
                  </div>
                  <p className="text-xs font-medium text-slate-500 mt-4 pt-3 border-t border-slate-200">
                    {t('taxes.vat.fromIncome')}
                  </p>
                </div>
                <div className="p-5 border border-slate-200 rounded-xl bg-slate-50 flex flex-col justify-between">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.05em] text-slate-500 mb-2">{t('taxes.vat.paid')}</div>
                    <div className="text-2xl sm:text-3xl font-black tracking-tight text-slate-950">
                      {formatCurrency(taxData.vat_paid)}
                    </div>
                  </div>
                  <p className="text-xs font-medium text-slate-500 mt-4 pt-3 border-t border-slate-200">
                    {t('taxes.vat.fromExpenses')}
                  </p>
                </div>
                <div className="p-5 rounded-xl bg-slate-50 border-2 border-slate-300 flex flex-col justify-between">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.05em] text-slate-500 mb-2">{t('taxes.vat.payable')}</div>
                    <div className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
                      {formatCurrency(taxData.vat_payable)}
                    </div>
                  </div>
                  <p className="text-xs font-medium text-slate-500 mt-4 pt-3 border-t border-slate-200">
                    {t('taxes.vat.formula')}
                  </p>
                </div>
              </div>
              <div className="mt-6 p-4 bg-blue-50/50 rounded-xl border border-blue-100 flex items-start gap-3">
                <Info className="h-5 w-5 text-blue-500 flex-shrink-0" />
                <p className="text-sm font-medium text-slate-700">
                  {t('taxes.vat.infoShort')}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Removed: Gross/Net Visual Breakdown - replaced by Transparente Berechnung section */}

          {/* Tax Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>{t('taxes.breakdown.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Income Tax */}
                {/* Income Tax - Different display for CH */}
                {country === "CH" ? (
                  <>
                    {/* Federal Tax */}
                    {taxData.federal_tax > 0 && (
                      <div className="border-b pb-4">
                        <div className="flex justify-between items-center mb-2">
                          <div className="flex items-center gap-2">
                            <div>
                              <h3 className="font-semibold text-gray-900">{t('taxes.breakdown.federalTax')}</h3>
                              <p className="text-sm text-gray-600">
                                {t('taxes.breakdown.federalTaxDesc')}
                              </p>
                            </div>
                            {taxData.calculation_steps && taxData.calculation_steps[7] && (
                              <TaxTooltip step={taxData.calculation_steps[7]} t={t} />
                            )}
                          </div>
                          <div className="text-2xl font-bold text-gray-900">
                            {formatCurrency(taxData.federal_tax)}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Cantonal Tax */}
                    {taxData.cantonal_tax > 0 && (
                      <div className="border-b pb-4">
                        <div className="flex justify-between items-center mb-2">
                          <div className="flex items-center gap-2">
                            <div>
                              <h3 className="font-semibold text-gray-900">{t('taxes.breakdown.cantonalTax')}</h3>
                              <p className="text-sm text-gray-600">
                                {taxData.canton_name || t('taxes.breakdown.cantonDefault')}
                              </p>
                            </div>
                            {taxData.calculation_steps && taxData.calculation_steps[8] && (
                              <TaxTooltip step={taxData.calculation_steps[8]} t={t} />
                            )}
                          </div>
                          <div className="text-2xl font-bold text-gray-900">
                            {formatCurrency(taxData.cantonal_tax)}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Total Income Tax for CH */}
                    <div className="border-b pb-4">
                      <div className="flex justify-between items-center mb-2">
                        <div>
                          <h3 className="font-semibold text-gray-900">{t('taxes.breakdown.incomeTaxTotal')}</h3>
                          <p className="text-sm text-gray-600">
                            {t('taxes.breakdown.incomeTaxTotalDesc')}
                          </p>
                        </div>
                        <div className="text-2xl font-bold text-gray-900" data-testid="income-tax-display">
                          {formatCurrency(taxData.income_tax)}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="border-b pb-4">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center gap-2">
                        <div>
                          <h3 className="font-semibold text-gray-900">{t('taxes.breakdown.incomeTax')}</h3>
                          <p className="text-sm text-gray-600">
                            {country === "AT" 
                              ? t('taxes.breakdown.incomeTaxAT') 
                              : t('taxes.breakdown.incomeTaxDE')}
                          </p>
                        </div>
                        {taxData.calculation_steps && (
                          country === "AT" ? taxData.calculation_steps[8] : taxData.calculation_steps[5]
                        ) && (
                          <TaxTooltip step={
                            country === "AT" ? taxData.calculation_steps[8] : taxData.calculation_steps[5]
                          } t={t} />
                        )}
                      </div>
                      <div className="text-2xl font-bold text-gray-900" data-testid="income-tax-display">
                        {formatCurrency(taxData.income_tax)}
                      </div>
                    </div>
                  </div>
                )}

                {/* Solidarity Surcharge - DE only */}
                {country === "DE" && taxData.solidarity_surcharge >= 0 && (
                  <div className="border-b pb-4">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center gap-2">
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {t('taxes.breakdown.solidaritySurcharge')}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {t('taxes.breakdown.solidaritySurchargeDesc')}
                          </p>
                        </div>
                        {taxData.calculation_steps && taxData.calculation_steps[7] && (
                          <TaxTooltip step={taxData.calculation_steps[7]} t={t} />
                        )}
                      </div>
                      <div className="text-2xl font-bold text-gray-900" data-testid="solidarity-surcharge-display">
                        {formatCurrency(taxData.solidarity_surcharge)}
                      </div>
                    </div>
                  </div>
                )}

                {/* Church Tax - DE only, conditional on user.church_tax - always show when toggle active */}
                {country === "DE" && user?.church_tax && (
                  <div className="border-b pb-4">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center gap-2">
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {t('taxes.breakdown.churchTax')}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {t('taxes.breakdown.churchTaxDesc')}
                          </p>
                        </div>
                        {taxData.calculation_steps && taxData.calculation_steps[6] && (
                          <TaxTooltip step={taxData.calculation_steps[6]} t={t} />
                        )}
                      </div>
                      <div className="text-2xl font-bold text-gray-900" data-testid="church-tax-display">
                        {formatCurrency(taxData.church_tax || 0)}
                      </div>
                    </div>
                  </div>
                )}

                {/* Trade Tax - DE only, conditional on taxData.trade_tax_liable - always show when toggle active */}
                {country === "DE" && taxData.trade_tax_liable && (
                  <div className="border-b pb-4">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center gap-2">
                        <div>
                          <h3 className="font-semibold text-gray-900">{t('taxes.breakdown.tradeTax')}</h3>
                          <p className="text-sm text-gray-600">
                            {t('taxes.breakdown.tradeTaxDesc')}
                          </p>
                        </div>
                        {taxData.calculation_steps && taxData.calculation_steps[8] && (
                          <TaxTooltip step={taxData.calculation_steps[8]} t={t} />
                        )}
                      </div>
                      <div className="text-2xl font-bold text-gray-900" data-testid="trade-tax-display">
                        {formatCurrency(taxData.trade_tax || 0)}
                      </div>
                    </div>
                  </div>
                )}

                {/* Social Security - AT and CH */}
                {(country === "AT" || country === "CH") && taxData.social_security > 0 && (
                  <div className="border-b pb-4">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center gap-2">
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {country === "CH" ? t('taxes.breakdown.socialSecurityCH') : t('taxes.breakdown.socialSecurity')}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {country === "CH" 
                              ? t('taxes.swiss.contribution')
                              : t('taxes.breakdown.socialSecurityAT')}
                          </p>
                        </div>
                        {taxData.calculation_steps && (
                          country === "AT" ? taxData.calculation_steps[6] : taxData.calculation_steps[5]
                        ) && (
                          <TaxTooltip step={
                            country === "AT" ? taxData.calculation_steps[6] : taxData.calculation_steps[5]
                          } t={t} />
                        )}
                      </div>
                      <div className="text-2xl font-bold text-gray-900" data-testid="social-security-display">
                        {formatCurrency(taxData.social_security)}
                      </div>
                    </div>
                  </div>
                )}

                {/* VAT */}
                <div className="pb-4">
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-2">
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {t('taxes.breakdown.vatLabel')}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {t('taxes.breakdown.vatDetails')} {formatCurrency(taxData.vat_collected)} | 
                          {t('taxes.breakdown.vatPaid')} {formatCurrency(taxData.vat_paid)}
                        </p>
                      </div>
                      {taxData.calculation_steps && (
                        (country === "DE" && taxData.calculation_steps[10]) ||
                        (country === "AT" && taxData.calculation_steps[9]) ||
                        (country === "CH" && taxData.calculation_steps[9])
                      ) && (
                        <TaxTooltip step={
                          country === "DE" ? taxData.calculation_steps[10] :
                          country === "AT" ? taxData.calculation_steps[9] :
                          taxData.calculation_steps[9]
                        } t={t} />
                      )}
                    </div>
                    <div className="text-2xl font-bold text-gray-900" data-testid="vat-payable-display">
                      {formatCurrency(taxData.vat_payable)}
                    </div>
                  </div>
                </div>

                {/* Total Tax Burden */}
                <div className="p-4 border-2 border-gray-900 bg-gray-50 rounded-xl">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">
                        {t('taxes.burden.total')}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {country === "CH" 
                          ? t('taxes.burden.ch')
                          : country === "AT"
                          ? t('taxes.burden.at')
                          : t('taxes.burden.de')}
                      </p>
                    </div>
                    <div className="text-3xl font-bold text-gray-900" data-testid="total-tax-burden-display">
                      {formatCurrency(taxData.total_tax_burden)}
                    </div>
                  </div>
                </div>

                {/* Effective Tax Rate */}
                <div className="p-4 border border-gray-200 bg-white rounded-xl">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">
                        {t('taxes.burden.effectiveRate')}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {t('taxes.burden.ratioDescription')}
                      </p>
                    </div>
                    <div className="text-3xl font-bold text-gray-900" data-testid="effective-tax-rate-display">
                      {taxData.taxable_income > 0
                        ? ((taxData.total_tax_burden / taxData.taxable_income) * 100).toFixed(2)
                        : "0.00"}
                      %
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Detailed Calculation Steps */}
          {taxData.calculation_steps && taxData.calculation_steps.length > 0 && (
            <Card className="border border-gray-200">
              <CardHeader className="border-b border-gray-100">
                <CardTitle className="text-base font-semibold text-gray-900 flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  {t('taxes.calculation.title')}
                </CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  {t('taxes.calculation.subtitle')}
                </p>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  {taxData.calculation_steps.map((step, index) => (
                    <div 
                      key={index}
                      className="border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1">
                            {step.name}
                          </h3>
                          <div className="text-xs font-mono text-gray-600 bg-gray-50 px-3 py-2 rounded border border-gray-200 mb-2">
                            {step.formula}
                          </div>
                        </div>
                        <div className="text-xl font-bold text-gray-900 ml-4">
                          {formatCurrency(step.amount)}
                        </div>
                      </div>
                      
                      <div className="text-sm text-gray-700 mb-2">
                        {step.explanation}
                      </div>
                      
                      {step.limitations && (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          <p className="text-xs text-gray-500 flex items-start gap-1">
                            <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                            <span>{step.limitations}</span>
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tax Optimization Tips */}
          <Card className="border border-gray-200">
            <CardHeader className="border-b border-gray-100">
              <CardTitle className="text-base font-semibold text-gray-900">
                {t('taxes.tips.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-3 text-sm text-gray-700">
                <div className="flex items-start space-x-3">
                  <div className="h-1.5 w-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p>
                    <strong className="text-gray-900">{t('taxes.tips.expenses')}:</strong> {t('taxes.tips.expensesDesc')}
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="h-1.5 w-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p>
                    <strong className="text-gray-900">{t('taxes.tips.depreciation')}:</strong> {t('taxes.tips.depreciationDesc')}
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="h-1.5 w-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p>
                    <strong className="text-gray-900">{t('taxes.tips.smallBusiness')}:</strong> {t('taxes.tips.smallBusinessDesc')}
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="h-1.5 w-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p>
                    <strong className="text-gray-900">{t('taxes.tips.pension')}:</strong> {t('taxes.tips.pensionDesc')}
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="h-1.5 w-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p>
                    <strong className="text-gray-900">{t('taxes.tips.advisor')}:</strong> {t('taxes.tips.advisorDesc')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

